import { useState } from 'react'
import { motion } from 'framer-motion'
import { useScrollAnimation, useCountAnimation } from '../../hooks/useScrollAnimation'
import CodeWindow from './CodeWindow'
import styles from './Hero.module.css'

const CONTRACT_ADDRESS = '4SyefSzhyyUDcYgBfGyLWCnXteo9GDqVDcpZQY7ypump'

const stats = [
  { value: 100, suffix: 'K+', label: 'Users Supported' },
  { value: 3, suffix: '', label: 'SDKs Available' },
  { value: 4, suffix: '+', label: 'LLM Providers' },
]

function AnimatedStat({ value, suffix, label, delay }: { value: number; suffix: string; label: string; delay: number }) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>()
  const count = useCountAnimation(value, 2000, isVisible)

  return (
    <motion.div
      ref={ref}
      className={styles.stat}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.5 }}
    >
      <span className={styles.statValue}>{count}{suffix}</span>
      <span className={styles.statLabel}>{label}</span>
    </motion.div>
  )
}

export default function Hero() {
  const [copied, setCopied] = useState(false)

  const handleCopyCA = async () => {
    await navigator.clipboard.writeText(CONTRACT_ADDRESS)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <section className={styles.hero}>
      <div className={styles.content}>
        <motion.div
          className={styles.badge}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.span
            className={styles.pulse}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.5, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          Context Data Platform
        </motion.div>

        <motion.h1
          className={styles.title}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Build <span className={styles.gradientText}>Production-Scale</span>
          <br />
          AI Agents with <motion.span
            className={styles.highlight}
            animate={{
              textShadow: [
                '0 0 20px rgba(255, 215, 0, 0.4)',
                '0 0 60px rgba(255, 215, 0, 0.6)',
                '0 0 20px rgba(255, 215, 0, 0.4)',
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >Luminox</motion.span>
        </motion.h1>

        <motion.p
          className={styles.description}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          The complete infrastructure for building, observing, and scaling cloud-native AI agents.
          Store context, track tasks, and enable self-learning — all in one platform.
        </motion.p>

        <motion.div
          className={styles.buttons}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <motion.a
            href="#quickstart"
            className="btn btn-primary btn-large"
            whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(255, 215, 0, 0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </motion.a>
          <motion.a
            href="https://github.com/luminox-mem/luminox#readme"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-large"
            whileHover={{ scale: 1.05, borderColor: 'rgba(255, 215, 0, 0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            Read Documentation
          </motion.a>
        </motion.div>

        <div className={styles.stats}>
          {stats.map((stat, index) => (
            <AnimatedStat
              key={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              label={stat.label}
              delay={0.6 + index * 0.1}
            />
          ))}
        </div>
      </div>

      <motion.div
        className={styles.visual}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <motion.div
          className={styles.caSection}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <span className={styles.caLabel}>CA:</span>
          <span className={styles.caAddress}>{CONTRACT_ADDRESS}</span>
          <motion.button
            className={styles.copyButton}
            onClick={handleCopyCA}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
            {copied ? 'Copied!' : 'Copy'}
          </motion.button>
        </motion.div>
        <motion.p
          className={styles.tokenMessage}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Memory Credit Discounts for Luminox Holders
        </motion.p>
        <CodeWindow />
      </motion.div>
    </section>
  )
}
